import { ensureError, isDefined } from '@fabric-app/utils/guards';
import { BaseStorage } from './BaseStorage.js';
import { captureException } from '@fabric-app/compat/capabilities/sentry';

class IndexedDBStorage extends BaseStorage {
  constructor() {
    super(...arguments);
    this.readCache = {};
  }
  async checkConnection() {
    try {
      await this.getConnection();
      return true;
    } catch {
      return false;
    }
  }
  async reconnect() {
    this.connection = null;
    await this.initializeConnection();
  }
  async initializeConnection(retryCount = 0) {
    try {
      this.connection = new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName);
        request.onsuccess = () => {
          const db = request.result;
          db.onclose = () => {
            console.warn("IndexedDB connection was unexpectedly closed. Attempting to reopen...");
            this.connection = null;
          };
          resolve(db);
        };
        request.onerror = (event) => {
          reject(ensureError(event));
        };
      });
      await this.connection;
      this.initializing = false;
      this.initializingPromise = null;
    } catch (error) {
      if (retryCount < this.MAX_RETRIES) {
        console.warn(`Error initializing IndexedDB connection. Retrying... (Attempt ${(retryCount + 1).toFixed(0)}/${this.MAX_RETRIES.toFixed(0)})`);
        await this.initializeConnection(retryCount + 1);
      } else {
        captureException(new Error("Failed to initialize IndexedDB connection"), {
          extra: {
            retries: this.MAX_RETRIES,
            error: ensureError(error)
          }
        });
        throw ensureError(error);
      }
    }
  }
  initializeReadConnection() {
    this.initializingRead = false;
    this.initializingReadPromise = null;
    return this.initializeConnection();
  }
  async initializeStore(storeName) {
    const db = await this.getConnection();
    if (!db.objectStoreNames.contains(storeName)) {
      const version = db.version + 1;
      db.close();
      this.connection = new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, version);
        request.onupgradeneeded = (event) => {
          const db2 = event.target.result;
          db2.createObjectStore(storeName);
        };
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = (event) => {
          reject(ensureError(request.error ?? event));
        };
        this.loadKeysToMemory(storeName).catch((e) => {
          captureException(ensureError(e), {
            level: "error"
          });
        });
      });
    }
  }
  async getStore(storeName, mode = "readonly") {
    try {
      await this.initializeStore(storeName);
      const db = await this.getConnection();
      return db.transaction(storeName, mode).objectStore(storeName);
    } catch (error) {
      captureException(new Error("Failed to open IndexedDB transaction"), {
        extra: {
          error: ensureError(error)
        }
      });
      return null;
    }
  }
  async flushReadBatchImpl(batchesToProcess) {
    for (const [storeName, keys] of Object.entries(batchesToProcess)) {
      const store = await this.getStore(storeName);
      if (!store) return;
      if (!isDefined(this.readCache[storeName])) {
        this.readCache[storeName] = {};
      }
      for (const key of keys) {
        this.addGetItemToReadCache(storeName, key);
      }
      const promises = Array.from(keys).map((key) => new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => {
          this.readCache[storeName][key] = request.result;
          resolve();
        };
        request.onerror = (event) => {
          delete this.readCache[storeName][key];
          reject(ensureError(request.error ?? event));
        };
      }));
      await Promise.allSettled(promises);
    }
  }
  async getItemImpl(storeName, key) {
    if (isDefined(this.readCache[storeName]) && key in this.readCache[storeName]) {
      const value = this.readCache[storeName][key];
      delete this.readCache[storeName][key];
      return Promise.resolve(value);
    }
    return Promise.resolve(null);
  }
  async setItemBatchImpl(storeName, items) {
    const store = await this.getStore(storeName, "readwrite");
    if (!store) return;
    return new Promise((resolve, reject) => {
      const transaction = store.transaction;
      for (const [key, value] of items) {
        store.put(value, key);
      }
      transaction.oncomplete = () => {
        resolve();
      };
      transaction.onerror = (event) => {
        reject(ensureError(transaction.error ?? event));
      };
    });
  }
  async removeItemImpl(storeName, key) {
    const store = await this.getStore(storeName, "readwrite");
    if (!store) return;
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = (event) => {
        reject(ensureError(request.error ?? event));
      };
    });
  }
  async clearImpl(storeName) {
    const store = await this.getStore(storeName, "readwrite");
    if (!store) return;
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = (event) => {
        reject(ensureError(request.error ?? event));
      };
    });
  }
  async keysImpl(storeName) {
    const store = await this.getStore(storeName);
    if (!store) return [];
    return new Promise((resolve, reject) => {
      const request = store.getAllKeys();
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = (event) => {
        reject(ensureError(request.error ?? event));
      };
    });
  }
  async *getAllItemsIteratorImpl(storeName) {
    const store = await this.getStore(storeName);
    if (!store) return;
    if ("getAll" in store) {
      const request = store.getAll();
      const items = await new Promise((resolve, reject) => {
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          reject(ensureError(request.error));
        };
      });
      for (const item of items) {
        yield item;
      }
    } else {
      const request = store.openCursor();
      while (true) {
        const cursor = await new Promise((resolve, reject) => {
          request.onsuccess = () => {
            resolve(request.result);
          };
          request.onerror = (event) => {
            reject(ensureError(event));
          };
        });
        if (cursor) {
          yield cursor.value;
          cursor.continue();
        } else {
          break;
        }
      }
    }
  }
  async getAllItemsImpl(storeName) {
    const items = [];
    for await (const item of this.getAllItemsIteratorImpl(storeName)) {
      items.push(item);
    }
    return items;
  }
  addGetItemToReadCache(storeName, key) {
    if (!this.readCache[storeName]) {
      this.readCache[storeName] = {};
    }
    this.readCache[storeName][key] = void 0;
  }
}

export { IndexedDBStorage };
//# sourceMappingURL=IndexedDBStorage.js.map
